import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { Alert, Button, Divider, Input, Sheet, Typography } from "@mui/joy";
import { useVenue } from "../../redux/venue/venueHooks";
import {
  formOpenSelector,
  selectedVenueIDsSelector,
  setFormOpen,
  setSelectedVenueIDs,
  setTmpGig,
} from "../../reducers/rhapsody";
import {
  useCreateVenueMutation,
  useDeleteVenueMutation,
  useUpdateVenueMutation,
} from "../../redux/venue/venueEndpoints";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { Venue_Entity } from "../../entities/venue";
import DialogClose from "../dialogClose/dialogClose";
import { useMyMember } from "../../redux/member/memberHooks";
import { GooglePlaceSuggestions } from "../googlePlaceSuggestions/googlePlaceSuggestions";
import { usePro } from "../usePro/usePro";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} FormVenue page
 */
export function FormVenue() {
  const venueIDs = useSelector(selectedVenueIDsSelector);
  const { venue } = useVenue(venueIDs);
  const open = useSelector(formOpenSelector("venue"));
  const gigFormOpen = useSelector(formOpenSelector("gig"));
  const [_venue, setVenue] = useState<Partial<Venue_Entity>>({});
  const pro = usePro();
  const [createVenue] = useCreateVenueMutation();
  const [updateVenue] = useUpdateVenueMutation();
  const [deleteVenue] = useDeleteVenueMutation();

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (venue?.id) {
      setVenue(venue);
    }
  }, [venue]);

  const onClose = () => {
    setVenue({});
    dispatch(setSelectedVenueIDs());
    dispatch(setFormOpen({ isOpen: false, formID: "venue" }));
  };

  const save = async () => {
    if (!pro)
      return dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: true }));
    if (venueIDs && _venue) {
      updateVenue({ id: venueIDs.id, body: _venue });
    } else if (_venue) {
      const promise = createVenue(_venue);
      // Quick-add from the Service form: select the new venue straight into the
      // open gig draft (the list query refreshes it into the dropdown).
      if (gigFormOpen) {
        try {
          const created = await promise.unwrap();
          if (created?.id) dispatch(setTmpGig({ venueID: created.id }));
        } catch (e) {
          console.error(e);
        }
      }
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: <Typography>You are about to delete a Venue.</Typography>,
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (venueIDs?.id) deleteVenue(venueIDs?.id);
    onClose();
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title={`${venueIDs?.id ? "Edit" : "Create"} Venue`}
          subtitle={
            !venueIDs?.id
              ? "Save the places you perform so future services fill in faster."
              : undefined
          }
        />
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 500,
          maxWidth: "100%",
        }}
      >
        {!pro ? (
          <Alert
            color="info"
            startDecorator={<i className="fa-regular fa-circle-info"></i>}
          >
            Add Venues you play outside of Rhapsody allowing you to manage all
            your work seamlessly in one platform.
          </Alert>
        ) : (
          []
        )}
        <GroupCaption>The basics</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          {_venue ? (
            <Input
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              variant="plain"
              autoFocus
              placeholder="e.g. Riverside Concert Hall"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-signature"
                  color={tileColors.blue}
                  label="Name"
                />
              }
              value={_venue.name}
              onChange={(e) =>
                setVenue((v) => ({ ...v, name: e.target.value }))
              }
              type="text"
            />
          ) : (
            []
          )}
        </Sheet>
        <GroupCaption>Location</GroupCaption>
        <GooglePlaceSuggestions
          onSelect={(e) => {
            setVenue((v) => ({
              ...v,
              address: e.address ? e.address : "",
              city: e.city ? e.city : "",
              state: e.state ? e.state : "",
              zipcode: e.zipcode ? e.zipcode : "",
              tzName: e.tzName ? e.tzName : "",
              longitude: e.longitude,
              latitude: e.latitude,
            }));
          }}
        />
        {_venue.tzName ? (
          <Sheet variant="soft" sx={groupSheetSx}>
            <Input
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-location-dot"
                  color={tileColors.orange}
                  label="Address"
                />
              }
              value={_venue.address}
              onChange={(e) =>
                setVenue((v) => ({ ...v, address: e.target.value }))
              }
              type="text"
            />
            <Divider />
            <Input
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-city"
                  color={tileColors.blue}
                  label="City"
                />
              }
              value={_venue.city}
              onChange={(e) =>
                setVenue((v) => ({ ...v, city: e.target.value }))
              }
              type="text"
            />
            <Divider />
            <Input
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-map"
                  color={tileColors.green}
                  label="State"
                />
              }
              value={_venue.state}
              onChange={(e) =>
                setVenue((v) => ({ ...v, state: e.target.value }))
              }
              type="text"
            />
            <Divider />
            <Input
              sx={{ background: "transparent" }}
              slotProps={{
                input: { style: { textAlign: "right" } },
              }}
              variant="plain"
              startDecorator={
                <FieldLabel
                  icon="fa-solid fa-hashtag"
                  color={tileColors.gray}
                  label="Zipcode"
                />
              }
              value={_venue.zipcode}
              onChange={(e) =>
                setVenue((v) => ({ ...v, zipcode: e.target.value }))
              }
              type="text"
            />
            {_venue?.tzName ? (
              <>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pl: 1.5,
                    pr: 1,
                    py: 1,
                  }}
                >
                  <FieldLabel
                    icon="fa-solid fa-globe"
                    color={tileColors.teal}
                    label="Timezone"
                  />
                  <Typography level="body2">
                    {_venue.latitude},{_venue.longitude} - {_venue.tzName}
                  </Typography>
                </Box>
              </>
            ) : (
              []
            )}
          </Sheet>
        ) : (
          []
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {venueIDs?.id ? (
          <Button
            variant="soft"
            onClick={askDelete}
            color="danger"
            sx={pillButtonSx}
          >
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={onClose}
          color="neutral"
          variant="plain"
          sx={pillButtonSx}
        >
          Cancel
        </Button>
        <Button
          sx={{ ...pillButtonSx, px: 3 }}
          endDecorator={
            !pro ? (
              <Typography
                sx={{ color: "white" }}
                variant="outlined"
                color="neutral"
                level="body5"
              >
                PRO
              </Typography>
            ) : undefined
          }
          onClick={save}
          color="info"
        >
          {venueIDs?.id ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
